import { useEntityFilter } from '@risksmart-app/components/src/contexts/entityFilterContext';
import type { ConsequenceRegisterResponseRow } from '@risksmart-app/trpc/src/types';
import type {
  Consequence_Bool_Exp,
  GetConsequencesQuery,
} from '@risksmart-app/web-graphql-client/generated/graphql';
import {
  GetConsequencesDocument,
  Parent_Type_Enum,
} from '@risksmart-app/web-graphql-client/generated/graphql';
import { createQueryHook } from 'src/utils';

import useEntityWhereFilter from '@/hooks/useEntityWhereFilter';

const useConsequenceGraphqlVariables = () => {
  const whereFilter = useEntityWhereFilter<Consequence_Bool_Exp>(
    Parent_Type_Enum.Consequence
  );

  return { where: whereFilter };
};

type InternalArgs = {
  entityIds?: string[];
};

type UseGetConsequenceRegisterArgs = Record<string, never>;

const useGetConsequenceRegisterBase = createQueryHook<
  InternalArgs,
  ConsequenceRegisterResponseRow[],
  GetConsequencesQuery
>({
  trpcQueryOptions: (trpc, { entityIds }) =>
    trpc.frontend.consequence.register.queryOptions({ entityIds }),
  mapTrpcDataToGraphQL: (data) => ({ consequence: data }),
  graphqlDocument: GetConsequencesDocument,
  graphqlVariables: useConsequenceGraphqlVariables,
});

/**
 * Fetches consequences with automatic entity filtering for both GraphQL and
 * tRPC paths. Entity filtering uses useEntityFilter context internally —
 * consumers don't need to pass entityIds.
 */
export const useGetConsequenceRegister = (params: {
  queryArgs: UseGetConsequenceRegisterArgs;
  shouldSkip?: boolean;
}) => {
  const { entityIds } = useEntityFilter();

  return useGetConsequenceRegisterBase({
    queryArgs: {
      entityIds: entityIds?.length ? entityIds : undefined,
    },
    shouldSkip: params.shouldSkip,
  });
};

/**
 * Maps TRPC consequence data to match the GraphQL query structure
 * Exported for use in data sources
 */
export function mapTrpcConsequencesToGraphQL(
  trpcData: ConsequenceRegisterResponseRow[] | undefined
): GetConsequencesQuery | undefined {
  if (!trpcData) {
    return undefined;
  }

  return {
    consequence: trpcData,
  };
}
